import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Eye, Code, Save, Upload } from 'lucide-react';

const AdminEmailTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { templates, updateTemplate, publishTemplate } = useEmailTemplates();
  
  const template = templates?.find(t => t.id === id);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setHtmlContent(template.html_content);
    }
  }, [template]);

  const handleSave = async () => {
    if (!template) return;
    setIsSaving(true);
    try {
      await updateTemplate.mutateAsync({
        id: template.id,
        updates: { name, description, html_content: htmlContent }
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!template) return;
    await publishTemplate.mutateAsync(template.id);
    navigate('/main/joia/admin/email-templates');
  };

  if (!template) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-white/60">Template não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/main/joia/admin/email-templates')}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {template.is_draft ? 'Editar Rascunho' : 'Editar Template'} (v{template.version})
            </h1>
            <p className="text-white/60 text-sm">
              {template.template_key} • {template.category}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
          >
            {showPreview ? <Code className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Editar' : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={isSaving}
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            Salvar
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!template.is_draft}
            className="bg-luxury-gold hover:bg-luxury-gold/90 text-black"
          >
            <Upload className="w-4 h-4 mr-2" />
            Publicar
          </Button>
        </div>
      </div>

      {!showPreview ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
          <div>
            <Label className="text-white">Nome</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-white">Descrição</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              rows={2}
            />
          </div>
          <div>
            <Label className="text-white">Conteúdo HTML</Label>
            <Textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              className="bg-white/5 border-white/10 text-white font-mono text-sm"
              rows={20}
            />
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-400 mb-2">Variáveis disponíveis</h3>
            <div className="flex flex-wrap gap-2">
              {template.variables?.map((v: string) => (
                <code key={v} className="text-xs bg-white/5 px-2 py-1 rounded border border-white/10 text-white/70">
                  {v}
                </code>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <iframe
            srcDoc={htmlContent}
            className="w-full h-[600px] bg-white rounded-lg"
            title="Template Preview"
          />
        </div>
      )}
    </div>
  );
};

export default AdminEmailTemplateEditor;
